import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { fetchUiPanelSettings } from '../api';
import {
  normalizeLayouts,
  normalizeResponse,
  persistPanelLayouts,
  persistPanelSettings,
  readCachedPanelLayouts,
  readCachedPanelSettings,
  resolveDescription,
  resolveTabLabel,
  resolveTitle,
  resolveVisibility
} from './panelSettingsState';

const EMPTY_OVERRIDES = {};

/**
 * Несохранённые черновики раскладок по вкладкам. Живут в ref, а не в состоянии: черновик
 * меняется на каждом шаге переноса, и состояние контекста перерисовывало бы на каждом шаге все
 * разделы. Нужен, чтобы правка переживала переход в другой раздел и обратно.
 */
const createDraftStore = () => {
  const drafts = new Map();
  return {
    get: (tab) => drafts.get(tab) ?? null,
    set: (tab, draft) => (draft ? drafts.set(tab, draft) : drafts.delete(tab)),
    clear: () => drafts.clear()
  };
};

const PanelSettingsContext = createContext({
  title: (panelId) => resolveTitle(EMPTY_OVERRIDES, panelId),
  description: (panelId) => resolveDescription(EMPTY_OVERRIDES, panelId),
  isVisible: (panelId) => resolveVisibility(EMPTY_OVERRIDES, panelId),
  tabLabel: (tabId, fallback) => resolveTabLabel(EMPTY_OVERRIDES, tabId, fallback),
  layoutFor: () => null,
  canEditLayout: false,
  layoutDrafts: createDraftStore(),
  isLoading: false,
  refresh: async () => {}
});

/**
 * Переименования и состав панелей интерфейса. Запрос уходит только после логина — до него
 * `/settings/ui-panels` вернёт 401, и глобальный обработчик выкинет из приложения.
 *
 * Стартовое состояние берётся из localStorage, а не из пустого объекта: при пустом умолчании
 * скрытая панель мелькала бы на каждой загрузке, а переименованная прыгала бы с исходного
 * названия на новое. Кэш — подсказка для первой отрисовки, ответ сервера всегда побеждает.
 *
 * Ошибка чтения не гасит интерфейс: остаются умолчания реестра, то есть всё видно и названия
 * исходные. Fail-open здесь обязателен — недоступность Postgres не должна оставлять
 * пользователя перед пустым дашбордом.
 */
const PanelSettingsProvider = ({ isAuthenticated = false, isAdmin = false, children }) => {
  const [overrides, setOverrides] = useState(readCachedPanelSettings);
  const [layouts, setLayouts] = useState(readCachedPanelLayouts);
  const [isLoading, setIsLoading] = useState(false);
  // Ленивое состояние, а не ref: хранилище создаётся один раз и читается во время отрисовки.
  const [layoutDrafts] = useState(createDraftStore);

  const apply = useCallback((payload) => {
    const next = normalizeResponse(payload);
    setOverrides(next);
    persistPanelSettings(next);

    // Ответ старого api раскладок не несёт — тогда оставляем, что было, а не стираем.
    if (payload && typeof payload === 'object' && 'layouts' in payload) {
      const nextLayouts = normalizeLayouts(payload.layouts);
      setLayouts(nextLayouts);
      persistPanelLayouts(nextLayouts);
    }
  }, []);

  const refresh = useCallback(
    async (known) => {
      if (!isAuthenticated) {
        setOverrides(EMPTY_OVERRIDES);
        setLayouts({});
        return;
      }

      // Эндпоинты записи возвращают состояние тенанта целиком, поэтому экран настроек передаёт
      // уже полученный ответ и второго запроса не будет.
      if (known !== undefined) {
        apply(known);
        return;
      }

      setIsLoading(true);
      try {
        apply(await fetchUiPanelSettings());
      } catch (error) {
        console.warn('Failed to load UI panel settings.', error);
      } finally {
        setIsLoading(false);
      }
    },
    [isAuthenticated, apply]
  );

  useEffect(() => {
    refresh();
  }, [refresh]);

  const value = useMemo(
    () => ({
      title: (panelId) => resolveTitle(overrides, panelId),
      description: (panelId) => resolveDescription(overrides, panelId),
      isVisible: (panelId) => resolveVisibility(overrides, panelId),
      tabLabel: (tabId, fallback) => resolveTabLabel(overrides, tabId, fallback),
      // Раскладку видят все, правит только админ. Сервер это проверяет сам; флаг здесь — чтобы
      // клиент не видел кнопки, которая ответила бы ему 403.
      layoutFor: (tab) => layouts[tab] ?? null,
      canEditLayout: isAdmin,
      layoutDrafts,
      isLoading,
      refresh
    }),
    [overrides, layouts, isAdmin, layoutDrafts, isLoading, refresh]
  );

  return <PanelSettingsContext.Provider value={value}>{children}</PanelSettingsContext.Provider>;
};

const usePanelSettings = () => useContext(PanelSettingsContext);

/** Сахар для одной панели: `{ title, description, visible }`. */
const usePanel = (panelId) => {
  const panels = usePanelSettings();
  return {
    title: panels.title(panelId),
    description: panels.description(panelId),
    visible: panels.isVisible(panelId)
  };
};

export { PanelSettingsContext, PanelSettingsProvider, usePanel, usePanelSettings };
